import React, { useState } from 'react';
import styled from 'styled-components';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, addDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

const ErrorContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const TestCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
`;

const TestButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
  margin-bottom: 1rem;

  &:hover {
    background: #357abd;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  color: #dc3545;
`;

const ErrorItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid #dc3545;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const ErrorTest = () => {
  const [testResults, setTestResults] = useState({
    auth: {
      invalidCredentials: null,
      accountNotFound: null,
      tooManyAttempts: null
    },
    firestore: {
      documentNotFound: null,
      permissionDenied: null,
      invalidQuery: null
    },
    storage: {
      uploadError: null,
      downloadError: null,
      deleteError: null
    },
    api: {
      networkError: null,
      timeoutError: null,
      serverError: null
    }
  });

  const [errors, setErrors] = useState([]);

  const logError = (category, type, error) => {
    const timestamp = new Date().toISOString();
    const errorLog = {
      timestamp,
      category,
      type,
      message: error.message,
      stack: error.stack
    };

    // In a production environment, you would send this to your logging service
    console.error('Error Log:', errorLog);

    setErrors(prev => [...prev, errorLog]);
    
    // Update test results
    setTestResults(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [type]: error.message
      }
    }));
  };

  const testAuthErrors = async () => {
    const auth = getAuth();

    // Test invalid credentials
    try {
      await signInWithEmailAndPassword(auth, 'test@example.com', 'wrongpassword');
    } catch (error) {
      logError('auth', 'invalidCredentials', error);
    }

    // Test account not found
    try {
      await signInWithEmailAndPassword(auth, 'nonexistent@example.com', 'password');
    } catch (error) {
      logError('auth', 'accountNotFound', error);
    }
  };

  const testFirestoreErrors = async () => {
    // Test document not found
    try {
      await getDoc(doc(db, 'nonexistent', 'doc123'));
    } catch (error) {
      logError('firestore', 'documentNotFound', error);
    }

    // Test permission denied
    try {
      await getDocs(collection(db, 'restricted'));
    } catch (error) {
      logError('firestore', 'permissionDenied', error);
    }

    // Test invalid query
    try {
      await addDoc(collection(db, 'test'), { timestamp: 'invalid-date' });
    } catch (error) {
      logError('firestore', 'invalidQuery', error);
    }
  };

  const testStorageErrors = async () => {
    // Test upload error
    try {
      const invalidFile = new Blob([''], { type: 'invalid/type' });
      const storageRef = ref(storage, 'test/invalid.file');
      await uploadBytes(storageRef, invalidFile);
    } catch (error) {
      logError('storage', 'uploadError', error);
    }

    // Test download error
    try {
      await getDownloadURL(ref(storage, 'nonexistent/file.txt'));
    } catch (error) {
      logError('storage', 'downloadError', error);
    }

    // Test delete error
    try {
      await deleteObject(ref(storage, 'nonexistent/file.txt'));
    } catch (error) {
      logError('storage', 'deleteError', error);
    }
  };

  const testAPIErrors = async () => {
    // Test network error
    try {
      await fetch('http://nonexistent.example.com');
    } catch (error) {
      logError('api', 'networkError', error);
    }

    // Test timeout error
    try {
      await Promise.race([
        fetch('/api/slow-endpoint'),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        )
      ]);
    } catch (error) {
      logError('api', 'timeoutError', error);
    }

    // Test server error
    try {
      const response = await fetch('/api/error-endpoint');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      logError('api', 'serverError', error);
    }
  };

  const runAllTests = async () => {
    setErrors([]);
    setTestResults({
      auth: { invalidCredentials: null, accountNotFound: null, tooManyAttempts: null },
      firestore: { documentNotFound: null, permissionDenied: null, invalidQuery: null },
      storage: { uploadError: null, downloadError: null, deleteError: null },
      api: { networkError: null, timeoutError: null, serverError: null }
    });

    await testAuthErrors();
    await testFirestoreErrors();
    await testStorageErrors();
    await testAPIErrors();
  };

  return (
    <ErrorContainer>
      <TestSection>
        <h3>Error Handling Tests</h3>
        <TestButton onClick={runAllTests}>
          Run All Error Tests
        </TestButton>

        <TestResults>
          <h3>Authentication Errors</h3>
          <TestGrid>
            <TestCard>
              <h4>Invalid Credentials</h4>
              <p>{testResults.auth.invalidCredentials || 'Not tested'}</p>
            </TestCard>
            <TestCard>
              <h4>Account Not Found</h4>
              <p>{testResults.auth.accountNotFound || 'Not tested'}</p>
            </TestCard>
            <TestCard>
              <h4>Too Many Attempts</h4>
              <p>{testResults.auth.tooManyAttempts || 'Not tested'}</p>
            </TestCard>
          </TestGrid>

          <h3>Firestore Errors</h3>
          <TestGrid>
            <TestCard>
              <h4>Document Not Found</h4>
              <p>{testResults.firestore.documentNotFound || 'Not tested'}</p>
            </TestCard>
            <TestCard>
              <h4>Permission Denied</h4>
              <p>{testResults.firestore.permissionDenied || 'Not tested'}</p>
            </TestCard>
            <TestCard>
              <h4>Invalid Query</h4>
              <p>{testResults.firestore.invalidQuery || 'Not tested'}</p>
            </TestCard>
          </TestGrid>

          <h3>Storage Errors</h3>
          <TestGrid>
            <TestCard>
              <h4>Upload Error</h4>
              <p>{testResults.storage.uploadError || 'Not tested'}</p>
            </TestCard>
            <TestCard>
              <h4>Download Error</h4>
              <p>{testResults.storage.downloadError || 'Not tested'}</p>
            </TestCard>
            <TestCard>
              <h4>Delete Error</h4>
              <p>{testResults.storage.deleteError || 'Not tested'}</p>
            </TestCard>
          </TestGrid>

          <h3>API Errors</h3>
          <TestGrid>
            <TestCard>
              <h4>Network Error</h4>
              <p>{testResults.api.networkError || 'Not tested'}</p>
            </TestCard>
            <TestCard>
              <h4>Timeout Error</h4>
              <p>{testResults.api.timeoutError || 'Not tested'}</p>
            </TestCard>
            <TestCard>
              <h4>Server Error</h4>
              <p>{testResults.api.serverError || 'Not tested'}</p>
            </TestCard>
          </TestGrid>

          {errors.length > 0 && (
            <>
              <h3>Error Logs</h3>
              <ErrorList>
                {errors.map((error, index) => (
                  <ErrorItem key={index}>
                    [{error.timestamp}] {error.category}.{error.type}: {error.message}
                  </ErrorItem>
                ))}
              </ErrorList>
            </>
          )}
        </TestResults>
      </TestSection>
    </ErrorContainer>
  );
};

export default ErrorTest; 